import "../../Components/Widgets/HomepageHero/HomepageHero.scss";
import Player from "@vimeo/player";

const Glide = window.Glide;
const AUTOPLAY_DURATION = 3000;
const ANIMATION_DURATION = 1500;
const VIMEO_ASPECT_RATIO = 16 / 9;

const setVimeoPoster = async (poster) => {
    if (!poster || poster.getAttribute("data-poster-loaded") === "true") {
        return;
    }

    const videoId = poster.getAttribute("data-vimeo-id");
    if (!videoId) {
        return;
    }

    try {
        const response = await fetch(
            `https://vimeo.com/api/oembed.json?url=https://vimeo.com/video/${encodeURIComponent(videoId)}&width=1280&height=720`
        );
        if (!response.ok) {
            return;
        }

        const data = await response.json();
        if (data?.thumbnail_url) {
            poster.src = data.thumbnail_url;
            poster.setAttribute("data-poster-loaded", "true");
        }
    } catch {
    }
};

const sizeVimeoFramesToCover = (mediaEl, frames) => {
    if (!mediaEl || !frames.length) {
        return;
    }

    const { width, height } = mediaEl.getBoundingClientRect();
    if (width < 1 || height < 1) {
        return;
    }

    let frameWidth;
    let frameHeight;

    if (width / height > VIMEO_ASPECT_RATIO) {
        frameWidth = width;
        frameHeight = width / VIMEO_ASPECT_RATIO;
    } else {
        frameHeight = height;
        frameWidth = height * VIMEO_ASPECT_RATIO;
    }

    frames.forEach((frame) => {
        frame.style.width = `${frameWidth}px`;
        frame.style.height = `${frameHeight}px`;
        frame.style.top = `${(height - frameHeight) / 2}px`;
        frame.style.left = `${(width - frameWidth) / 2}px`;
    });
};

const setUpHomepageHero = () => {
    document.querySelectorAll("[data-homepage-hero]:not([data-homepage-hero-ready])").forEach((hero) => {
        hero.setAttribute("data-homepage-hero-ready", "true");

        const pauseButton = hero.querySelector("[data-homepage-hero-pause]");
        const slider = hero.querySelector("[data-homepage-hero-slider]");
        const videoGroup = hero.querySelector("[data-homepage-hero-videos]");
        const mediaEl = hero.querySelector(".homepage-hero__media");
        let glide = null;
        let paused = false;
        let html5Videos = [];
        let vimeoPlayers = [];
        let activeIndex = 0;
        let vimeoInitialized = false;
        let vimeoFrames = [];

        const setPausedUi = (isPaused) => {
            paused = isPaused;
            if (!pauseButton) {
                return;
            }
            pauseButton.setAttribute("aria-pressed", isPaused ? "true" : "false");
            pauseButton.setAttribute("aria-label", isPaused ? "Play hero media" : "Pause hero media");
        };

        const showVideo = (index) => {
            html5Videos.forEach((video, i) => {
                video.classList.toggle("is-active", i === index);
                if (i !== index) {
                    video.pause();
                    video.currentTime = 0;
                }
            });
            vimeoPlayers.forEach((item, i) => {
                item.frame.classList.toggle("is-active", i === index);
                if (i !== index) {
                    item.player.pause().catch(() => {});
                }
            });
            activeIndex = index;
        };

        const syncVimeoCover = () => {
            sizeVimeoFramesToCover(mediaEl, vimeoFrames);
        };

        const initVimeoPlayers = () => {
            if (vimeoInitialized || !videoGroup || videoGroup.getAttribute("data-homepage-hero-videos") !== "vimeo") {
                return;
            }

            vimeoInitialized = true;
            hero.classList.add("homepage-hero--vimeo-playing");

            vimeoFrames = Array.from(videoGroup.querySelectorAll("[data-homepage-hero-vimeo-frame]"));
            syncVimeoCover();

            if (typeof ResizeObserver !== "undefined" && mediaEl) {
                const observer = new ResizeObserver(() => syncVimeoCover());
                observer.observe(mediaEl);
            } else {
                window.addEventListener("resize", syncVimeoCover);
            }

            vimeoFrames.forEach((frame, index) => {
                const iframe = frame.querySelector("[data-homepage-hero-vimeo]");
                if (!iframe) {
                    return;
                }

                if (!iframe.getAttribute("src")) {
                    iframe.src = iframe.getAttribute("data-src");
                }

                const player = new Player(iframe);
                vimeoPlayers.push({ frame, player });
                player.on("ended", () => {
                    if (paused || vimeoPlayers.length < 2) {
                        return;
                    }
                    const next = (index + 1) % vimeoPlayers.length;
                    showVideo(next);
                    vimeoPlayers[next].player.play().catch(() => {});
                });
            });

            if (vimeoPlayers[0]) {
                vimeoPlayers[0].player.play().catch(() => {});
            }
        };

        if (slider && Glide) {
            glide = new Glide(slider, {
                type: "carousel",
                perView: 1,
                autoplay: AUTOPLAY_DURATION,
                animationDuration: ANIMATION_DURATION,
                gap: 0,
            });
            glide.mount();
        }

        if (videoGroup && videoGroup.getAttribute("data-homepage-hero-videos") === "html5") {
            html5Videos = Array.from(videoGroup.querySelectorAll("[data-homepage-hero-video]"));
            html5Videos.forEach((video, index) => {
                video.muted = true;
                video.playsInline = true;
                video.addEventListener("ended", () => {
                    if (paused || html5Videos.length < 2) {
                        return;
                    }
                    const next = (index + 1) % html5Videos.length;
                    showVideo(next);
                    html5Videos[next].play().catch(() => {});
                });
            });
            if (html5Videos[0]) {
                html5Videos[0].play().catch(() => {});
            }
        }

        if (videoGroup && videoGroup.getAttribute("data-homepage-hero-videos") === "vimeo") {
            const poster = videoGroup.querySelector("[data-homepage-hero-vimeo-poster]");
            setVimeoPoster(poster);
            initVimeoPlayers();
        }

        if (pauseButton) {
            pauseButton.addEventListener("click", () => {
                const nextPaused = !paused;
                setPausedUi(nextPaused);

                if (glide) {
                    if (nextPaused) {
                        glide.pause();
                    } else {
                        glide.play();
                    }
                }

                if (html5Videos.length) {
                    const current = html5Videos[activeIndex];
                    if (nextPaused) {
                        current?.pause();
                    } else {
                        current?.play().catch(() => {});
                    }
                }

                if (vimeoPlayers.length) {
                    const current = vimeoPlayers[activeIndex];
                    if (nextPaused) {
                        current?.player.pause().catch(() => {});
                    } else {
                        current?.player.play().catch(() => {});
                    }
                }
            });
        }
    });
};

setUpHomepageHero();
window.addEventListener("load", setUpHomepageHero);

export { setUpHomepageHero };
